import React, { useEffect, useState } from "react";
import { App } from "./App";
import { rootStore, useMst } from "./store/root";
import { observer } from "mobx-react";
import { Form, Input, Typography } from "antd";
import styles from "./AuthContainer.module.scss";
import { MainButton } from "./components/baseComponents/MainButton/MainButton";
import {
  EmailIcon,
  HomePageDiamondIcon,
  PasswordIcon,
} from "./components/baseComponents/Icons";
import { LinkOut } from "./components/baseComponents";
import { environment } from "./store/utils/consts";
import { getVersion } from "./jsxProviders/helpers/tmpDir";
import { ConfigureAmplify } from "./config/amplifyConfig";
import { EnvironmentInfo, EnvironmentDebugPanel } from "./components/EnvironmentInfo";
import { useEnvironment } from "./hooks/useEnvironment";
import { getWebSocketUrl } from "./utils/serverUrl";

export const AuthContainer = observer(() => {
  const {
    authStore: {
      userEmail,
      handleTotpCodeOrFirstLogin,
      isSignedIn,
      signIn,
      lastSignInErrorMessage,
      specialAuthState,
      isSigninInProgress,
    },
  } = useMst();

  const envInfo = useEnvironment();
  const [message, setMessage] = useState<string[]>([]);
  const [websocket, setWebsocket] = useState<WebSocket | null>(null);
  const [listeningForSaml, setListeningForSaml] = useState<boolean>(false);

  useEffect(() => {
    // Clean up on unmount
    return () => {
      if (websocket) {
        websocket.close();
      }
    };
  }, []);

  const handleSaml = () => {
    setListeningForSaml(true);
    if (websocket) {
      websocket.send(
        JSON.stringify({
          type: "auth",
          token: "saml",
        })
      );
    } else {
      // Establish the WebSocket connection and set up handlers
      const wsUrl = getWebSocketUrl();
      console.log(`Connecting to WebSocket: ${wsUrl} (${envInfo.environment} environment)`);
      const ws = new WebSocket(wsUrl);
      setWebsocket(ws);

      ws.onopen = () => {
        console.log(`Connected to the WebSocket server: ${wsUrl}`);
      };

      ws.onmessage = (event) => {
        if (!listeningForSaml) {
          return;
        }
        setMessage(event.data);
        const resData = JSON.parse(JSON.parse(event.data).data.sessionParams);
        const { provider } = resData;
        ConfigureAmplify(provider, true);
        Object.keys(resData).forEach((key) => {
          localStorage.setItem(key, resData[key]);
        });
        location.reload();
      };
      ws.onclose = () => {
        console.log("Disconnected from the WebSocket server");
        setWebsocket(null);
      };
    }
  };
  const [version, setVersion] = React.useState<string | undefined>(undefined);
  useEffect(() => {
    rootStore.authStore.startListeningToAmplifyAuth();
  }, []);
  useEffect(() => {
    const loadVersion = async () => {
      try {
        const jsxVersion = await getVersion();
        setVersion(jsxVersion);
      } catch (e) {
        console.log(e);
      }
    };
    loadVersion();
    (window as any).LOG_LEVEL = "INFO";
  }, []);
  const hasSpecialAuthState = !!(
    specialAuthState === "totp" || specialAuthState === "newPassword"
  );
  return isSignedIn && userEmail ? (
    <>
      <App />
      <EnvironmentInfo />
      <EnvironmentDebugPanel />
    </>
  ) : listeningForSaml ? (
    <div className={styles.AuthContainer}>
      <div className={styles.Header} style={{ marginTop: "30vh" }}>
        {message.length ? "Login in" : "Waiting for external authentication"}
      </div>
      <MainButton
        text="Cancel"
        style={{ alignSelf: "center" }}
        type="submit"
        className={styles.LoginButton}
        onClick={() => setListeningForSaml(false)}
      />
    </div>
  ) : (
    /*<AmplifySignOut />*/
    <div className={styles.AuthContainer}>
      <HomePageDiamondIcon className={styles.Diamond} />
      <div className={styles.Header}>
        Get started with <span style={{ color: "#ff154D" }}>Blings.io</span>
        <div className={styles.SubHeader}>Login to your account</div>
      </div>
      <div className={styles.Form}>
        <Form
          name="basic"
          initialValues={{ remember: true }}
          onFinish={({ username, password, code }) => {
            hasSpecialAuthState
              ? handleTotpCodeOrFirstLogin(code)
              : signIn(username, password);
          }}
          autoComplete="off"
          className={styles.LoginForm}
        >
          {hasSpecialAuthState ? (
            <>
              <Form.Item
                name="code"
                rules={[
                  {
                    required: true,
                    message:
                      specialAuthState === "totp"
                        ? "Please insert the TOTP code"
                        : "Please create a new password",
                  },
                ]}
              >
                <div
                  style={{ display: "flex", gap: "2rem", alignItems: "center" }}
                >
                  <Input
                    type="text"
                    placeholder={
                      specialAuthState === "totp" ? "TOTP code" : "New Password"
                    }
                  />
                </div>
              </Form.Item>
              <MainButton
                text="Sign in"
                type="submit"
                disabled={isSigninInProgress}
                className={styles.LoginButton}
              />
              <Form.ErrorList
                errors={[
                  <Typography.Text type="danger">
                    {lastSignInErrorMessage}
                  </Typography.Text>,
                ]}
              />
            </>
          ) : (
            <>
              <Form.Item
                name="username"
                rules={[
                  { required: true, message: "Please insert user email!" },
                ]}
              >
                <div
                  style={{ display: "flex", gap: "2rem", alignItems: "center" }}
                >
                  <EmailIcon />
                  <Input type="email" placeholder="Username" />
                </div>
              </Form.Item>
              <Form.Item
                className={styles.PasswordInput}
                name="password"
                rules={[
                  { required: true, message: "Please insert your password!" },
                ]}
              >
                <div
                  style={{ display: "flex", gap: "2rem", alignItems: "center" }}
                >
                  <PasswordIcon />
                  <Input.Password placeholder="Password" />
                </div>
              </Form.Item>
              <MainButton
                text="Sign in"
                type="submit"
                disabled={isSigninInProgress}
                className={styles.LoginButton}
              />
              <Form.ErrorList
                errors={[
                  <Typography.Text type="danger">
                    {lastSignInErrorMessage}
                  </Typography.Text>,
                ]}
              />
            </>
          )}
        </Form>
        <div className={styles.ForgotPassword}>
          Reset password on Platform.{" "}
          <LinkOut
            className={styles.Link}
            link="https://platform.blings.io/"
            text="Click here"
          />
        </div>
        <div className={styles.ForgotPassword} onClick={handleSaml}>
          SAML Sign in.{" "}
          <LinkOut
            className={styles.Link}
            link="https://platform.blings.io/auth/sign-in/extension"
            text="Click here"
          />
        </div>
      </div>
      <div className={styles.Version}>
        {version} {environment !== "production" && environment}
      </div>
    </div>
  );
});
